import api_fetch from '../utils/api_fetch';
import { storageHandler } from '../utils/storage_handler';
import createServiceClient from './createServiceClient';

const zoeShipmentTrackingService = {
  async getList(page = 1, limit = 20) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ offset, limit });

    return (
      await api_fetch.get(
        `/v1/shipment-tracking?${params.toString()}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  },

  async searchByListField(
    filters = {},
    page = 1,
    sortBy,
    sortOrder,
    limit = 20
  ) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams();

    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    if (
      sortBy &&
      sortOrder &&
      sortBy !== 'default' &&
      sortOrder !== 'default'
    ) {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }

    const response = await api_fetch.get(
      `/v1/search-shipment?${params.toString()}`,
      {
        Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
      },
      {}
    );

    return response.json();
  },

  async getShipmentTracking(id) {
    return (
      await api_fetch.get(
        `/v1/shipment-tracking/${id}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  },

  async updateShipmentTracking(id, data) {
    return (
      await api_fetch.put(
        `/v1/shipment-tracking/${id}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {},
        '',
        data
      )
    ).json();
  },

  async getShipmentsByStatus(
    status,
    page = 1,
    limit = 20,
    additionalFilters = {},
    sortBy = null,
    sortOrder = null
  ) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ offset, limit });

    // Add any additional filters
    Object.entries(additionalFilters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    // Matches getShipmentsByDate. Without this the page's Sort By / Sort Order
    // controls rendered and re-triggered a search, but the values were never
    // sent — so changing the sort visibly did nothing. The backend already
    // accepts and whitelists these.
    if (
      sortBy &&
      sortOrder &&
      sortBy !== 'default' &&
      sortOrder !== 'default'
    ) {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }

    return (
      await api_fetch.get(
        `/v1/shipments-by-status/${encodeURIComponent(status)}?${params.toString()}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  },

  async getShipmentsByDate(
    dateType,
    page = 1,
    limit = 20,
    additionalFilters = {},
    sortBy = null,
    sortOrder = null
  ) {
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ offset, limit });

    // Add any additional filters
    Object.entries(additionalFilters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    // Add sorting parameters if provided
    if (
      sortBy &&
      sortOrder &&
      sortBy !== 'default' &&
      sortOrder !== 'default'
    ) {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }

    return (
      await api_fetch.get(
        `/v1/shipments-by-date/${encodeURIComponent(dateType)}?${params.toString()}`,
        {
          Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
        },
        {}
      )
    ).json();
  }
};

/**
 * New service client using factory pattern
 * Provides standard methods with centralized boilerplate
 */
export const zoeShipmentTrackingClient = createServiceClient({
  baseEndpoint: '/v1/shipment-tracking',
  searchEndpoint: '/v1/search-shipment',
  customMethods: {
    // Alias standard methods for backward compatibility
    searchByListField: (client) => client.search,
    getShipmentTracking: (client) => client.getById,
    updateShipmentTracking: (client) => client.update,

    // Custom method for getting shipments by status
    getShipmentsByStatus: (client) => async (
      status,
      page = 1,
      limit = 20,
      additionalFilters = {}
    ) => {
      const { offset, limit: lim } = client.helpers.getPaginationOffset(page, limit);
      const params = client.helpers.buildQueryParams({ offset, limit: lim });

      client.helpers.applyFilters(params, additionalFilters);

      return client.rawGet(`/v1/shipments-by-status/${encodeURIComponent(status)}?${params.toString()}`);
    },

    // Custom method for getting shipments by date
    getShipmentsByDate: (client) => async (
      dateType,
      page = 1,
      limit = 20,
      additionalFilters = {},
      sortBy = null,
      sortOrder = null
    ) => {
      const { offset, limit: lim } = client.helpers.getPaginationOffset(page, limit);
      const params = client.helpers.buildQueryParams({ offset, limit: lim });

      client.helpers.applyFilters(params, additionalFilters);
      client.helpers.applySorting(params, sortBy, sortOrder);

      return client.rawGet(`/v1/shipments-by-date/${encodeURIComponent(dateType)}?${params.toString()}`);
    }
  }
});

export default zoeShipmentTrackingService;
